import { useSearchParams, Link } from 'react-router-dom'
import { FileText, Video, User, Search as SearchIcon } from 'lucide-react'
import { useSearch } from '@/hooks/useSearch'
import { timeAgo } from '@/lib/utils'
import { CATEGORY_LABELS } from '@/lib/config'
import type { SearchResult } from '@/types'

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  document: <FileText size={16} className="text-cerah-teal" />,
  video:    <Video size={16} className="text-blue-500" />,
  contact:  <User size={16} className="text-purple-500" />,
}

const SOURCE_LABELS: Record<string, string> = {
  document: 'Dokumen',
  video:    'Video',
  contact:  'Kontak',
}

function getResultPath(r: SearchResult): string {
  if (r.source === 'video')   return '/app/kanal-video'
  if (r.source === 'contact') return `/app/direktori-kontak/${r.id}`
  return `/app/category/${r.category}`
}

export default function Search() {
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') ?? ''
  const { results, loading } = useSearch(query)

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-xl font-bold text-gray-800 dark:text-white mb-1">Hasil Pencarian</h1>
      {query && (
        <p className="text-sm text-gray-400 mb-6">
          {loading ? 'Mencari...' : `${results.length} hasil untuk "${query}"`}
        </p>
      )}

      {!query ? (
        <div className="flex flex-col items-center py-20 text-center">
          <SearchIcon size={40} className="text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">Masukkan kata kunci di kolom pencarian.</p>
        </div>
      ) : loading ? (
        <div className="flex flex-col gap-3">
          {[...Array(6)].map((_, i) => <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />)}
        </div>
      ) : results.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-center">
          <SearchIcon size={40} className="text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">Tidak ada hasil untuk "{query}"</p>
          <p className="text-xs text-gray-400 mt-1">Coba kata kunci yang berbeda.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {results.map(r => (
            <Link
              key={`${r.source}-${r.id}`}
              to={getResultPath(r)}
              className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-cerah-teal/30 hover:shadow-sm transition-all"
            >
              <div className="w-9 h-9 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                {SOURCE_ICONS[r.source]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 px-1.5 py-0.5 rounded font-medium">
                    {SOURCE_LABELS[r.source]}
                  </span>
                  {r.category && (
                    <span className="text-[10px] text-gray-400">
                      {CATEGORY_LABELS[r.category] ?? r.category}
                    </span>
                  )}
                </div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white line-clamp-1">{r.title}</p>
                {r.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{r.description}</p>
                )}
                <p className="text-[10px] text-gray-400 mt-1.5">{timeAgo(r.created_at)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
